import {
  type ChangeEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  useCallback,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import type { FocusEvent } from "react";
import {
  type GridFilterItem,
  type GridRenderHeaderFilterProps,
} from "@mui/x-data-grid-premium";

import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useGridRootProps } from "@mui/x-data-grid-pro/hooks/utils/useGridRootProps";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { BaseInput } from "src/components/BaseInput/BaseInput.tsx";
import { Flex } from "src/components/Flex.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { logger } from "src/logger";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { useDebouncedCallback } from "@exonn/hooks";
import { arrReplaceAtOrInsertInTheEnd } from "@exonn/utils";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { toJS } from "mobx";

export const DataGridNumberHeaderFilterImpl = observer(
  (props: GridRenderHeaderFilterProps) => {
    const { colDef, tabIndex, item, inputRef } = props;
    const store = useDataGridStore();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );

    const [draftValue, setDraftValue] = useState<string | null>(null);

    const { t } = useTranslation();
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();

    const disableColumnHeaderFilter =
      !!rootProps.slotProps?.headerFilterCell?.InputComponentProps?.disabled;

    const applyFilterChanges = useCallback(
      (updatedItem: GridFilterItem) => {
        if (!updatedItem.value && !appliedItem) {
          logger.debug("deleting header filter", {
            item: updatedItem,
          });
          apiRef.current.deleteFilterItem(updatedItem);
        } else {
          logger.debug("upserting header filter", {
            item: updatedItem,
          });
          apiRef.current.upsertFilterItem(updatedItem);
        }
      },
      [apiRef, appliedItem],
    );

    const [applyFilterChangesDebounced, cancelDebounceCallback] =
      useDebouncedCallback(applyFilterChanges, 300);

    const setInputRef = (node: HTMLInputElement | null) => {
      // @ts-ignore eslint-disable-next-line
      inputRef.current = node;
    };

    const handleFocus = () => {
      setDraftValue(item.value ?? appliedItem?.value ?? "");
      apiRef.current.startHeaderFilterEditMode(colDef.field);
    };

    const handleBlur = (event: FocusEvent) => {
      setDraftValue(null);
      apiRef.current.stopHeaderFilterEditMode();
      // Blurring an input element should reset focus state only if `relatedTarget` is not the header filter cell
      if (!event.relatedTarget?.className.includes("columnHeader")) {
        apiRef.current.setState((state) => ({
          ...state,
          focus: {
            cell: null,
            columnHeader: null,
            columnHeaderFilter: null,
            columnGroupHeader: null,
          },
        }));
      }
    };

    const applyValue = (value: string) => {
      const nextValue = processInputValue(value);
      cancelDebounceCallback();
      const nextItem = {
        ...item,
        value: nextValue,
      };

      const mergedFilterModel = mergeFilterModels(
        apiRef.current.state.filter.filterModel,
        toJS(store.appliedFilterModel),
      );

      const prevItems = mergedFilterModel.items;

      const nextItems = arrReplaceAtOrInsertInTheEnd(
        prevItems,
        (prevItem) => prevItem.field === nextItem.field,
        nextItem,
      );

      const filteredItems = nextItems.filter((item) => {
        return item.value != null && item.value !== "";
      });

      const nextFilterModel = {
        ...mergedFilterModel,
        items: filteredItems,
      };

      if (rootProps.filterMode === "server") {
        store.setAppliedFilterModel(nextFilterModel);

        // resetting 'not applied filters'
        apiRef.current.setFilterModel({
          items: [],
        });
      } else {
        apiRef.current.setFilterModel(nextFilterModel);
      }

      setTimeout(() => {
        setDraftValue(null);
        (document.activeElement as HTMLElement | null)?.blur();
      });
    };

    const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        applyValue(e.currentTarget.value);
      }
    };

    const handleInputValueChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      const nextValue = processInputValue(value);

      setDraftValue(nextValue);
      const nextItem = {
        ...item,
        value: nextValue,
      };
      applyFilterChangesDebounced(nextItem);
    };
    const filterMode = rootProps.filterMode;

    const getDisplayedValue = () => {
      if (draftValue != null) {
        return draftValue;
      }
      if (item.value != null) {
        return item.value;
      }
      if (appliedItem?.value != null) {
        return appliedItem.value;
      }
      // empty string fallback is important,
      // item.value can be undefined and if we pass undefined as value prop to input element,
      // then we will enter uncontrolled mode
      return "";
    };
    const inputValue = getDisplayedValue();
    const hasValue = inputValue != null && inputValue !== "";

    return (
      <Wrapper disabled={disableColumnHeaderFilter}>
        <Input
          disabled={disableColumnHeaderFilter}
          tabIndex={tabIndex}
          ref={setInputRef}
          value={inputValue}
          onChange={handleInputValueChange}
          placeholder={t("header-filters.operator.between", {
            ns: TranslationNamespaceEnum.DataGridComponent,
          })}
          onKeyUp={handleKeyUp}
          onFocus={handleFocus}
          onBlur={handleBlur}
          endAdornment={
            <Flex
              style={{
                marginRight: 7,
              }}
              flexShrink={0}
              gap={"5px"}
              alignItems={"center"}
            >
              {hasValue && filterMode === "server" && (
                <SearchButton
                  className={"search-button"}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyValue(getDisplayedValue());
                  }}
                />
              )}
              {hasValue && filterMode === "server" && (
                <ClearButton
                  className={"clear-button"}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyValue("");
                  }}
                />
              )}
            </Flex>
          }
        />
      </Wrapper>
    );
  },
);

const Input = styled(BaseInput)`
  .search-button {
    display: none;
  }
  .clear-button {
    display: none;
  }
  :hover {
    .search-button {
      display: flex;
    }
    .clear-button {
      display: flex;
    }
  }
`;

const ClearButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      css={(theme: ExactTheme) => css`
        display: flex;
        flex-shrink: 0;
        background-color: transparent;
        color: ${theme.colorsV2.grey[500]};
        align-items: center;
        border: none;
        padding: 0;
        cursor: pointer;

        :hover {
          color: ${theme.colorsV2.red[500]};
        }
      `}
    >
      <i className="icon-15-clear_x" />
    </button>
  );
};

const SearchButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      css={(theme: ExactTheme) => css`
        display: flex;
        flex-shrink: 0;
        background-color: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        :hover {
          color: ${theme.colorsV2.blue[500]};
        }
      `}
    >
      <i className={"icon-15-search"} />
    </button>
  );
};

const Wrapper = styled.div<{ disabled: boolean | undefined }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  margin-top: -1px;
  // 1px padding is needed to not cut off input outline
  padding: 1px 10px 10px;

  ${({ disabled }) =>
    disabled && {
      opacity: 0.4,
      pointerEvents: "none",
    }}
`;

const processInputValue = (value: string): string => {
  return value;
};
